import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@foundation/components/ui/card";
import { Button } from "@foundation/components/ui/button";
import { Badge } from "@foundation/components/ui/badge";
import { ScrollArea } from "@foundation/components/ui/scroll-area";
import { Stepper } from "@foundation/components/stepper";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@foundation/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@foundation/components/ui/select";
import {
  ArrowLeft,
  ArrowRight,
  Download,
  FileText,
  CheckCircle,
  Eye,
  ChevronDown,
  Loader2,
  Palette,
  Upload,
  Type,
  Image,
  Save,
  Info,
  Edit3,
  Check,
} from "lucide-react";
import { useToast } from "@foundation/hooks/use-toast";
import { useUser } from "@foundation/contextapi/UserContext";
import { useWorkspaceContext } from "@foundation/hooks/useWorkspaceContext";
import ScopingAILayout from "../components/RevisrLayout";
import { generatePDFFromPreview } from "../utils/pdfGenerator";
import { generateEnhancedDOCX } from "../utils/docxGenerator";
import { convertContentToBlocks } from "../utils/documentUtils";
import BrandingService from "@foundation/services/brandingService";
import { updatePolicyMarkdown } from "../services/quickCompareService";
import { finalize as finalizePolicyGeneration, updateStep as updatePolicyStep } from "../services/policyGenerationService";
import { policyTeableService } from "../services/policyTeableService";
import YooptaTemplateEditor from "../components/editor/YooptaTemplateEditor";
import YooptaMarkdownEditor from "../components/editor/YooptaMarkdownEditor";
import { splitMarkdownIntoSections } from "../utils/sectionUtils";
import { SortableSectionList } from "../components/editor/shared/SortableSectionList";
import remarkGfm from 'remark-gfm';

interface Change {
  id: string;
  type: 'text' | 'format' | 'logo';
  content: string;
  oldContent?: string;
  newContent?: string;
  lineNumber: number;
  section: string;
  status: 'pending' | 'accepted' | 'reverted';
}

interface Page {
  id: string;
  number: number;
  content: string;
}

// Section type for editing and preview
type EditorSection = {
  id: string;
  title: string;
  content: string;
  edited?: boolean;
};

export default function PreviewExport() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();

  const [changes, setChanges] = useState<Change[]>([]);
  const [documentContent, setDocumentContent] = useState<string>("");
  const [finalContent, setFinalContent] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [pages, setPages] = useState<Page[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Section-based state (replaces pages for better UX)
  const [sections, setSections] = useState<EditorSection[]>([]);
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [editorKey, setEditorKey] = useState(0);
  const [workflowType, setWorkflowType] = useState<string>("");
  const [policyId, setPolicyId] = useState<string | null>(null);
  const [policyTitle, setPolicyTitle] = useState<string>("");
  const [sourceRoute, setSourceRoute] = useState<string>("/revisr/policies"); // Default to policies page
  const [activeTab, setActiveTab] = useState("preview");
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isSavingToRepository, setIsSavingToRepository] = useState(false);
  const [savedToRepository, setSavedToRepository] = useState(false);
  const { user } = useUser();
  const { currentWorkspace } = useWorkspaceContext();
  const orgId = currentWorkspace?.id || '';

  // Branding state variables
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>("");
  const [hasPolicyBranding, setHasPolicyBranding] = useState<boolean>(false);
  const [fontFamily, setFontFamily] = useState<string>("Inter");
  const [primaryColor, setPrimaryColor] = useState<string>("#1a365d");
  const [secondaryColor, setSecondaryColor] = useState<string>("#374151");
  const [defaultColor, setDefaultColor] = useState<string>("#333333");
  const [isSavingBranding, setIsSavingBranding] = useState(false);

  // Editor state
  const [isSavingMarkdown, setIsSavingMarkdown] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [localMarkdown, setLocalMarkdown] = useState<string>("");

  // Document info state
  const [editedPolicyName, setEditedPolicyName] = useState<string>("");
  const [isSavingPolicyName, setIsSavingPolicyName] = useState(false);
  const [policyCreatedAt, setPolicyCreatedAt] = useState<string>("");
  const [policyStatus, setPolicyStatus] = useState<string>("");

  // Reference for document container (used for PDF generation)
  const documentContainerRef = useRef<HTMLDivElement>(null);

  // Template definitions with styling
  const templateStyles = {
    'iso-9001': {
      name: 'ISO 9001',
      titleStyle: 'text-3xl font-bold text-black tracking-wide border-b-4 border-black pb-3 mb-6',
      headingStyle: 'text-xl font-bold text-black border-l-4 border-black pl-4 mb-3',
      paragraphStyle: 'text-gray-800 leading-relaxed ml-4 mb-4',
      sectionSpacing: 'space-y-6',
    },
    'iso-14001': {
      name: 'ISO 14001',
      titleStyle: 'text-3xl font-bold text-green-900 border-b-4 border-green-600 pb-3 mb-6',
      headingStyle: 'text-xl font-bold text-green-800 border-l-4 border-green-600 pl-4 mb-3',
      paragraphStyle: 'text-gray-800 leading-relaxed ml-4 mb-4',
      sectionSpacing: 'space-y-6',
    },
    'esg-reporting': {
      name: 'ESG Reporting',
      titleStyle: 'text-3xl font-bold text-blue-900 border-b-4 border-blue-600 pb-3 mb-6',
      headingStyle: 'text-xl font-bold text-blue-800 border-l-4 border-blue-600 pl-4 mb-3',
      paragraphStyle: 'text-gray-800 leading-relaxed ml-4 mb-4',
      sectionSpacing: 'space-y-6',
    },
    'osha-compliance': {
      name: 'OSHA Compliance',
      titleStyle: 'text-3xl font-bold text-red-900 border-b-4 border-red-600 pb-3 mb-6',
      headingStyle: 'text-xl font-bold text-red-800 border-l-4 border-red-600 pl-4 mb-3',
      paragraphStyle: 'text-gray-800 leading-relaxed ml-4 mb-4',
      sectionSpacing: 'space-y-6',
    },
  };

  // Function to generate formatted document by applying template to uploaded document
  const generateFormattedDocument = (auditData: any, templateId: string) => {
    // Check if it's a custom template (starts with 'custom-')
    const isCustomTemplate = templateId.startsWith('custom-');

    // Use default styling for custom templates or get predefined template
    const template = isCustomTemplate
      ? {
        name: 'Custom Template',
        titleStyle: 'text-3xl font-bold text-gray-900 border-b-4 border-gray-600 pb-3 mb-6',
        headingStyle: 'text-xl font-bold text-gray-800 border-l-4 border-gray-600 pl-4 mb-3',
        paragraphStyle: 'text-gray-800 leading-relaxed ml-4 mb-4',
        sectionSpacing: 'space-y-6',
      }
      : (templateStyles[templateId as keyof typeof templateStyles] || templateStyles['iso-9001']);

    // Extract file name from uploaded document
    const fileName = auditData.oldAudit?.file?.name || 'Compliance Document';
    const documentTitle = fileName.replace(/\.[^/.]+$/, '').toUpperCase();

    // Generate formatted HTML content with template styling
    const formattedContent = `
      <div class="${template.sectionSpacing}">
        <div class="${template.titleStyle}">
          <h1>${documentTitle}</h1>
        </div>

        <p class="text-gray-800 leading-relaxed font-medium mb-6">
          This document has been formatted according to the ${template.name} compliance template.
          The following sections detail our organization's compliance with industry standards
          and regulatory requirements.
        </p>

        <div class="${template.sectionSpacing}">
          <div class="${template.headingStyle}">
            <h2>1. Executive Summary</h2>
          </div>
          <p class="${template.paragraphStyle}">
            Our compliance framework ensures adherence to all applicable regulations
            while maintaining operational efficiency and risk management protocols.
            This comprehensive approach includes regular audits, staff training, and
            continuous monitoring of regulatory changes.
          </p>
        </div>

        <div class="${template.sectionSpacing}">
          <div class="${template.headingStyle}">
            <h2>2. Regulatory Framework</h2>
          </div>
          <p class="${template.paragraphStyle}">
            This section covers the regulatory landscape and our systematic approach
            to compliance monitoring and reporting. We maintain compliance with multiple
            regulatory bodies including industry-specific standards and international
            guidelines as outlined in ${template.name}.
          </p>
        </div>

        <div class="${template.sectionSpacing}">
          <div class="${template.headingStyle}">
            <h2>3. Risk Assessment</h2>
          </div>
          <p class="${template.paragraphStyle}">
            Our risk assessment methodology identifies potential compliance risks and
            implements appropriate mitigation strategies. This includes regular risk
            evaluations and updates to our compliance procedures in accordance with
            ${template.name} requirements.
          </p>
        </div>

        <div class="${template.sectionSpacing}">
          <div class="${template.headingStyle}">
            <h2>4. Training and Awareness</h2>
          </div>
          <p class="${template.paragraphStyle}">
            All staff members receive comprehensive compliance training tailored to their
            roles and responsibilities. Regular refresher courses ensure ongoing awareness
            of compliance requirements and best practices as specified in ${template.name}.
          </p>
        </div>

        <div class="${template.sectionSpacing}">
          <div class="${template.headingStyle}">
            <h2>5. Monitoring and Reporting</h2>
          </div>
          <p class="${template.paragraphStyle}">
            We maintain robust monitoring systems to track compliance metrics and generate
            regular reports for management and regulatory bodies. This includes automated
            alerts for potential compliance issues in line with ${template.name} standards.
          </p>
        </div>

        <div class="${template.sectionSpacing}">
          <div class="${template.headingStyle}">
            <h2>6. Continuous Improvement</h2>
          </div>
          <p class="${template.paragraphStyle}">
            Our compliance program is subject to continuous review and improvement. We
            regularly assess the effectiveness of our procedures and implement enhancements
            based on industry best practices and ${template.name} regulatory updates.
          </p>
        </div>

        <div class="${template.sectionSpacing}">
          <div class="${template.headingStyle}">
            <h2>7. Documentation and Record Keeping</h2>
          </div>
          <p class="${template.paragraphStyle}">
            We maintain comprehensive documentation of all compliance activities, including
            audit reports, training records, and incident logs. All records are stored securely
            and are readily accessible for regulatory inspections as required by ${template.name}.
          </p>
        </div>

        <div class="${template.sectionSpacing}">
          <div class="${template.headingStyle}">
            <h2>8. Conclusion</h2>
          </div>
          <p class="${template.paragraphStyle}">
            This document demonstrates our commitment to maintaining the highest standards
            of compliance in accordance with ${template.name}. We will continue to monitor,
            review, and improve our compliance framework to ensure ongoing adherence to all
            applicable regulations and industry best practices.
          </p>
        </div>
      </div>
    `;

    return formattedContent;
  };

  useEffect(() => {
    // Check for policy ID in URL params
    const urlPolicyId = searchParams.get('id');

    // Check workflow type
    const workflow = sessionStorage.getItem('workflow_type');
    setWorkflowType(workflow || '');

    // Determine source route for the Back button
    const storedChanges = sessionStorage.getItem('reviewedChanges');
    const storedContent = sessionStorage.getItem('finalDocumentContent');
    const sessionPolicyReviewId = sessionStorage.getItem('policy_review_id');
    const auditDataString = sessionStorage.getItem('auditComparisonData');

    // Check if this is the create-policy export flow (URL path or session data)
    const isCreatePolicyExport = window.location.pathname.includes('/create-policy/export');
    const createPolicyReviewId = sessionStorage.getItem('create_policy_policy_review_id');
    const isCreatePolicyFlow = isCreatePolicyExport || (workflow === 'create_policy' && createPolicyReviewId);

    // Determine where the user came from
    if (isCreatePolicyFlow && (urlPolicyId || createPolicyReviewId)) {
      const effectiveId = urlPolicyId || createPolicyReviewId || '';
      setSourceRoute(`/revisr/create-policy/edit?id=${effectiveId}`);
      setWorkflowType('create_policy');

      // Sync sessionStorage if coming from URL
      if (urlPolicyId && !createPolicyReviewId) {
        sessionStorage.setItem('create_policy_policy_review_id', urlPolicyId);
      }
    } else if (urlPolicyId) {
      // If URL has policy ID and matches session, user came from workflow
      if (storedChanges && sessionPolicyReviewId === urlPolicyId) {
        // Came from EditAndApprove (workflow finalization)
        setSourceRoute("/revisr/edit-approve");
      } else if (storedContent && !storedChanges && sessionPolicyReviewId === urlPolicyId) {
        // Came from ScanAndReview (no changes found case)
        setSourceRoute("/revisr/scan-review");
      } else {
        // Came from PolicyDocuments (View/Edit button) or Dashboard
        setSourceRoute("/revisr/policies");
      }
    } else if (workflow === 'create_policy') {
      // Legacy create_policy flow without URL param
      if (createPolicyReviewId) {
        setSourceRoute(`/revisr/create-policy/edit?id=${createPolicyReviewId}`);
      } else {
        setSourceRoute("/revisr/template-selection");
      }
    } else if (storedChanges) {
      // Came from EditAndApprove (legacy flow without URL param)
      setSourceRoute("/revisr/edit-approve");
    } else if (storedContent) {
      // Came from ScanAndReview (no changes found case, legacy flow)
      setSourceRoute("/revisr/scan-review");
    } else {
      // Default to policies page
      setSourceRoute("/revisr/policies");
    }

    // If we have a URL policy ID, check if it's a completed policy to view
    // or if we're coming from the workflow to finalize
    const loadPolicyById = async (id: string) => {
      try {
        setPolicyId(id);

        // Fetch the policy record from Teable
        const statusCheck = await policyTeableService.getPolicy(id);

        // If we have session data and the ID matches, this is a finalization flow
        // BUT skip if policy is already completed
        if (storedChanges && sessionPolicyReviewId === id && statusCheck?.status !== 'completed') {
          console.log('[PreviewExport] 🚀 Starting finalization flow for policy:', id);
          let policyName = 'Compliance Document';
          if (auditDataString) {
            const auditData = JSON.parse(auditDataString);
            policyName = auditData.policyFileName || auditData.oldAudit?.file?.name || policyName;
          }
          setPolicyTitle(policyName);

          const parsedChanges = JSON.parse(storedChanges);
          setChanges(parsedChanges);
          let content = storedContent || '';

          // If content is empty, try to fetch from policy_markdown_url stored in Teable
          (async () => {
            if (!content && id) {
              try {
                const policyData = statusCheck ?? await policyTeableService.getPolicy(id);

                const contentPath = policyData?.policy_markdown_url || policyData?.markdown_url;
                if (contentPath) {
                  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
                  const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
                  const contentResponse = await fetch(`${supabaseUrl}/storage/v1/object/public/knowledge_docs/${contentPath}`, {
                    headers: {
                      'apikey': supabaseKey,
                      'Authorization': `Bearer ${supabaseKey}`,
                    },
                  });
                  if (contentResponse.ok) {
                    const fetchedContent = await contentResponse.text();
                    sessionStorage.setItem('finalDocumentContent', fetchedContent);
                    setDocumentContent(fetchedContent);
                    handleFinalizeDocument(parsedChanges, fetchedContent, policyName);
                    return;
                  }
                }
              } catch (fetchError) {
                console.warn('Could not fetch markdown content from Teable policy record:', fetchError);
              }
            }

            // If we didn't fetch or fetch failed, use existing content
            setDocumentContent(content);
            handleFinalizeDocument(parsedChanges, content, policyName);
          })();
          return;
        }

        // If we had session data but policy is completed, log and continue to view-only path
        if (storedChanges && sessionPolicyReviewId === id && statusCheck?.status === 'completed') {
          console.log('[PreviewExport] ⏭️  Skipping finalization - policy already completed');
        }

        // Otherwise, this is a view-only request - fetch the full policy from Teable
        const policyData = statusCheck ?? await policyTeableService.getPolicy(id);

        if (!policyData) {
          console.error('Policy not found in Teable for id:', id);
          toast({
            title: "Policy not found",
            description: "The requested policy could not be found.",
            variant: "destructive",
          });
          navigate("/revisr/policies");
          return;
        }


        setPolicyTitle(policyData.policy_name || 'Compliance Document');
        setPolicyStatus(policyData.status || 'draft');
        setPolicyCreatedAt(policyData.created_at || '');

        // Load branding data if it exists
        const hasBrandingData = policyData.branding_data &&
          typeof policyData.branding_data === 'object' &&
          Object.keys(policyData.branding_data).length > 0;

        if (hasBrandingData) {
          try {
            const branding = typeof policyData.branding_data === 'string'
              ? JSON.parse(policyData.branding_data)
              : policyData.branding_data;

            console.log('[PreviewExport] 📦 Using policy-specific branding data');
            setHasPolicyBranding(true);

            // Prefer file URLs over base64 previews if available
            if (branding.logoUrl) {
              const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
              setLogoPreview(`${supabaseUrl}/storage/v1/object/public/knowledge_docs/${branding.logoUrl}`);
            } else if (branding.logoPreview) {
              setLogoPreview(branding.logoPreview);
            }

            if (branding.fontFamily) setFontFamily(branding.fontFamily);
            if (branding.primaryColor) setPrimaryColor(branding.primaryColor);
            if (branding.secondaryColor) setSecondaryColor(branding.secondaryColor);
            if (branding.defaultColor) setDefaultColor(branding.defaultColor);
          } catch (e) {
            console.error('Error parsing branding data:', e);
            setHasPolicyBranding(false);
          }
        } else {
          console.log('[PreviewExport] 🔄 No policy branding data, will use org branding fallback');
          setHasPolicyBranding(false);
        }

        // For completed policies, fetch the markdown content from storage
        if (policyData.markdown_url) {
          try {
            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
            const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

            const contentResponse = await fetch(`${supabaseUrl}/storage/v1/object/public/knowledge_docs/${policyData.markdown_url}`, {
              headers: {
                'apikey': supabaseKey,
                'Authorization': `Bearer ${supabaseKey}`,
              },
            });

            if (contentResponse.ok) {
              const markdownContent = await contentResponse.text();
              setFinalContent(markdownContent);
              setLocalMarkdown(markdownContent);
              setDocumentContent(markdownContent);
              extractPages(markdownContent, []);
            } else {
              throw new Error('Failed to fetch markdown content');
            }
          } catch (contentError) {
            console.error('Error fetching policy content:', contentError);
          }

          // Fetch and parse the changes JSON if it exists to get real counts
          if (policyData.json_changes_url) {
            try {
              const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
              const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

              const changesResponse = await fetch(`${supabaseUrl}/storage/v1/object/public/knowledge_docs/${policyData.json_changes_url}`, {
                headers: {
                  'apikey': supabaseKey,
                  'Authorization': `Bearer ${supabaseKey}`,
                },
              });

              if (changesResponse.ok) {
                const changesData = await changesResponse.json();
                if (Array.isArray(changesData)) {
                  setChanges(changesData);
                }
              }
            } catch (changesError) {
              console.error('Error fetching changes content:', changesError);
            }
          }
        }

        setIsLoading(false);
      } catch (err) {
        console.error('Error in loadPolicyById:', err);
        setIsLoading(false);
        toast({
          title: "Error",
          description: "An unexpected error occurred while loading the policy.",
          variant: "destructive",
        });
      }
    };

    // Handle create-policy export flow with URL ?id= param
    if (isCreatePolicyFlow && (urlPolicyId || createPolicyReviewId)) {
      const effectiveId = urlPolicyId || createPolicyReviewId || '';
      const createPolicyContent = sessionStorage.getItem('create_policy_generated_content');
      const createPolicyName = sessionStorage.getItem('create_policy_policy_name');

      // Update step to 4 and status to 'completed'
      if (user?.id) {
        updatePolicyStep(effectiveId, 4, user.id, orgId, 'completed').catch((err: unknown) => {
          console.error('Error updating step to 4:', err);
        });
      }

      // Try sessionStorage first, then fall back to DB
      if (createPolicyContent) {
        setPolicyId(effectiveId);
        setPolicyTitle(createPolicyName || 'Generated Policy');
        setEditedPolicyName(createPolicyName || 'Generated Policy');
        setDocumentContent(createPolicyContent);
        setFinalContent(createPolicyContent);
        setLocalMarkdown(createPolicyContent);
        extractPages(createPolicyContent, []);
        setIsLoading(false);
        } else {
        // Load from Teable
        (async () => {
          try {
            const policyData = await policyTeableService.getPolicy(effectiveId);

            if (!policyData) {
              console.error('Policy not found in Teable for id:', effectiveId);
              toast({ title: "Error", description: "Could not load policy data.", variant: "destructive" });
              navigate("/revisr/policies");
              return;
            }

            setPolicyId(effectiveId);
            setPolicyTitle(policyData.policy_name || 'Generated Policy');
            setEditedPolicyName(policyData.policy_name || 'Generated Policy');
            setPolicyStatus(policyData.status || 'completed');
            setPolicyCreatedAt(policyData.created_at || '');

            // Load branding
            if (policyData.branding_data && typeof policyData.branding_data === 'object' && Object.keys(policyData.branding_data).length > 0) {
              try {
                const bd = typeof policyData.branding_data === 'string' ? JSON.parse(policyData.branding_data) : policyData.branding_data;
                setHasPolicyBranding(true);
                if (bd.logoUrl) {
                  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
                  setLogoPreview(`${supabaseUrl}/storage/v1/object/public/knowledge_docs/${bd.logoUrl}`);
                } else if (bd.logoPreview) {
                  setLogoPreview(bd.logoPreview);
                }
                if (bd.fontFamily) setFontFamily(bd.fontFamily);
                if (bd.primaryColor) setPrimaryColor(bd.primaryColor);
                if (bd.secondaryColor) setSecondaryColor(bd.secondaryColor);
                if (bd.defaultColor) setDefaultColor(bd.defaultColor);
              } catch (e) { console.error('Error parsing branding:', e); }
            }

            // Load markdown content from storage
            if (policyData.markdown_url) {
              const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
              const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
              const contentResponse = await fetch(
                `${supabaseUrl}/storage/v1/object/public/knowledge_docs/${policyData.markdown_url}`,
                { headers: { 'apikey': supabaseKey, 'Authorization': `Bearer ${supabaseKey}` } }
              );
              if (contentResponse.ok) {
                const markdownContent = await contentResponse.text();
                setDocumentContent(markdownContent);
                setFinalContent(markdownContent);
                setLocalMarkdown(markdownContent);
                sessionStorage.setItem('create_policy_generated_content', markdownContent);
                extractPages(markdownContent, []);
              }
            }

            setIsLoading(false);
          } catch (err) {
            console.error('Error loading create-policy export data:', err);
            setIsLoading(false);
          }
        })();
      }
      return;
    }

    // If URL has policy ID (non create-policy flow), use that flow
    if (urlPolicyId) {
      loadPolicyById(urlPolicyId);
      return;
    }

    // Legacy flow: Load reviewed changes from session storage (no URL param)
    if (storedChanges) {
      const parsedChanges = JSON.parse(storedChanges);
      setChanges(parsedChanges);
      let content = storedContent || '';

      let policyName = 'Compliance Document';
      if (auditDataString) {
        const auditData = JSON.parse(auditDataString);
        policyName = auditData.policyFileName || auditData.oldAudit?.file?.name || policyName;
      }
      setPolicyTitle(policyName);

      // If content is empty, try to fetch from policy_markdown_url via policy_review_id (Teable)
      (async () => {
        if (!content && sessionPolicyReviewId) {
          try {
            const policyData = await policyTeableService.getPolicy(sessionPolicyReviewId);
            const contentPath = policyData?.policy_markdown_url || policyData?.markdown_url;

            if (contentPath) {
              const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
              const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
              const contentResponse = await fetch(`${supabaseUrl}/storage/v1/object/public/knowledge_docs/${contentPath}`, {
                headers: {
                  'apikey': supabaseKey,
                  'Authorization': `Bearer ${supabaseKey}`,
                },
              });
              if (contentResponse.ok) {
                const fetchedContent = await contentResponse.text();
                sessionStorage.setItem('finalDocumentContent', fetchedContent);
                setDocumentContent(fetchedContent);
                handleFinalizeDocument(parsedChanges, fetchedContent, policyName);
                return;
              }
            }
          } catch (fetchError) {
            console.warn('Could not fetch markdown content from Teable policy record:', fetchError);
          }
        }

        // If we didn't fetch or fetch failed, use existing content
        setDocumentContent(content);
        handleFinalizeDocument(parsedChanges, content, policyName);
      })();
    } else if (workflow === 'create_policy') {
      // Legacy create_policy flow (template selection, no URL param)
      const auditData = sessionStorage.getItem('auditComparisonData');
      const selectedTemplate = sessionStorage.getItem('selectedTemplate');

      if (auditData && selectedTemplate) {
        const parsedData = JSON.parse(auditData);
        const formattedContent = generateFormattedDocument(parsedData, selectedTemplate);

        setDocumentContent(formattedContent);
        setFinalContent(formattedContent);
        setLocalMarkdown(formattedContent);
        extractPages(formattedContent, []);
        setIsLoading(false);
      } else {
        toast({
          title: "No document data found",
          description: "Please upload a document and select a template first.",
          variant: "destructive",
        });
        navigate("/revisr/new-audit");
      }
    } else {
      toast({
        title: "No review data found",
        description: "Please complete the review process first.",
        variant: "destructive",
      });
      navigate("/revisr/edit-approve");
    }
  }, [navigate, toast, searchParams]);

  // Global Branding Fetching
  useEffect(() => {
    const fetchGlobalBranding = async () => {
      // Skip if policy already has branding data
      if (hasPolicyBranding) {
        console.log('[PreviewExport] ⏭️  Skipping org branding fetch - policy has its own branding');
        return;
      }

      if (!orgId) return;

      try {
        console.log(`[PreviewExport] 🏢 Fetching global branding for org: ${orgId}`);
        const globalBranding = await BrandingService.getOrganizationBranding(orgId);

        if (globalBranding) {
          console.log('[PreviewExport] ✅ Global branding found:', globalBranding);

          // Only apply global defaults if current local state is empty/default
          // This avoids overwriting policy-specific settings if we're revisiting a policy

          // Helper to check if color is default or empty
          const isColorEmpty = (color: string) => !color || color === '#1a365d' || color === '#374151' || color === '#333333';

          if (!logoPreview && globalBranding.logo_url) {
            setLogoPreview(BrandingService.getLogoUrlWithCacheBusting(globalBranding.logo_url));
          }

          if (fontFamily === 'Inter' && globalBranding.font_family) {
            setFontFamily(globalBranding.font_family);
          }

          if (isColorEmpty(primaryColor) && globalBranding.primary_color) {
            setPrimaryColor(globalBranding.primary_color);
          }

          if (isColorEmpty(secondaryColor) && globalBranding.secondary_color) {
            setSecondaryColor(globalBranding.secondary_color);
          }

          if (isColorEmpty(defaultColor) && globalBranding.font_color) {
            setDefaultColor(globalBranding.font_color);
          }
        }
      } catch (error) {
        console.error('[PreviewExport] ❌ Error fetching global branding:', error);
      }
    };

    // Run branding fetch - will exit early if hasPolicyBranding is true
    // No timer needed since hasPolicyBranding is set synchronously in loadPolicyById
    fetchGlobalBranding();
  }, [hasPolicyBranding, logoPreview, fontFamily, primaryColor, secondaryColor, defaultColor]);

  const extractPages = (content: string, _reviewedChanges: Change[]) => {
    // Legacy page extraction (kept for backward compatibility)
    const charsPerPage = 2500;
    const minCharsPerPage = 500;

    // Step 1: Split by H2 sections (## ) as major semantic breaks
    const sections = content.split(/(?=^## )/gm);

    const extractedPages: Page[] = [];
    let currentContent = "";
    let currentPageNum = 1;

    for (const section of sections) {
      // If adding this section doesn't exceed page limit, add it to current page
      if (currentContent.length + section.length <= charsPerPage) {
        currentContent += (currentContent ? '\n\n' : '') + section;
      } else {
        // Current page would be too large

        // If current page has content and meets minimum, save it
        if (currentContent.trim().length >= minCharsPerPage) {
          extractedPages.push({
            id: `page-${currentPageNum}`,
            number: currentPageNum,
            content: currentContent.trim()
          });
          currentPageNum++;
          currentContent = section;
        } else {
          // Current page too small, need to split section
          const combined = currentContent + (currentContent ? '\n\n' : '') + section;

          // Split by paragraphs (double newline)
          const paragraphs = combined.split(/\n\n+/);
          currentContent = "";

          for (const para of paragraphs) {
            if (currentContent.length + para.length <= charsPerPage) {
              currentContent += (currentContent ? '\n\n' : '') + para;
            } else {
              // Save current page if it has content
              if (currentContent.trim().length > 0) {
                extractedPages.push({
                  id: `page-${currentPageNum}`,
                  number: currentPageNum,
                  content: currentContent.trim()
                });
                currentPageNum++;
              }
              currentContent = para;
            }
          }
        }
      }
    }

    // Add final page
    if (currentContent.trim()) {
      extractedPages.push({
        id: `page-${currentPageNum}`,
        number: currentPageNum,
        content: currentContent.trim()
      });
    }

    // Fallback for empty or very small content
    if (extractedPages.length === 0 && content.trim()) {
      extractedPages.push({
        id: "page-1",
        number: 1,
        content: content.trim()
      });
    }

    setPages(extractedPages);
    setCurrentPage(1);

    // NEW: Also extract sections for section-based navigation
    extractSections(content);
  };

  // Extract sections from markdown content
  const extractSections = (content: string) => {
    if (!content.trim()) {
      setSections([]);
      setSelectedSectionId(null);
      return;
    }

    const parsedSections = splitMarkdownIntoSections(content);
    const editorSections: EditorSection[] = parsedSections.map((s, i) => ({
      id: `section-${i}`,
      title: s.title,
      content: s.content,
      edited: s.isEdited,
    }));

    setSections(editorSections);
    if (editorSections.length > 0) {
      setSelectedSectionId(editorSections[0].id);
    }
  };

  // Section handlers
  const handleSectionSelect = (sectionId: string) => {
    setSelectedSectionId(sectionId);
    setEditorKey((k) => k + 1);
  };

  const handleSectionReorder = (reordered: EditorSection[]) => {
    const updated = reordered.map((s, i) => ({ ...s, id: `section-${i}` }));
    setSections(updated);
    updateContentFromSections(updated);
  };

  const handleSectionTitleChange = (sectionId: string, newTitle: string) => {
    const updated = sections.map((s) =>
      s.id === sectionId ? { ...s, title: newTitle, edited: true } : s
    );
    setSections(updated);
    updateContentFromSections(updated);
  };

  const handleSectionDelete = (sectionId: string) => {
    if (sections.length <= 1) {
      toast({
        title: 'Cannot delete',
        description: 'You must have at least one section.',
        variant: 'destructive',
      });
      return;
    }

    const updated = sections.filter((s) => s.id !== sectionId).map((s, i) => ({ ...s, id: `section-${i}` }));
    setSections(updated);
    if (selectedSectionId === sectionId) {
      setSelectedSectionId(updated.length > 0 ? updated[0].id : null);
    }
    updateContentFromSections(updated);
  };

  const handleSectionAdd = () => {
    const newId = `section-${sections.length}`;
    const updated = [...sections, { id: newId, title: 'New Section', content: '', edited: true }];
    setSections(updated);
    setSelectedSectionId(newId);
    setEditorKey((k) => k + 1);
    updateContentFromSections(updated);
  };

  const handleSectionAddAfter = (afterSectionId: string) => {
    const idx = sections.findIndex((s) => s.id === afterSectionId);
    if (idx === -1) return;
    const newSection: EditorSection = { id: 'temp', title: 'New Section', content: '', edited: true };
    const updated = [...sections.slice(0, idx + 1), newSection, ...sections.slice(idx + 1)]
      .map((s, i) => ({ ...s, id: `section-${i}` }));
    const newId = `section-${idx + 1}`;
    setSections(updated);
    setSelectedSectionId(newId);
    setEditorKey((k) => k + 1);
    updateContentFromSections(updated);
  };

  const handleSectionEdit = (sectionId: string) => {
    setSelectedSectionId(sectionId);
    setActiveTab('editor');
    setEditorKey((k) => k + 1);
  };

  const handleSectionContentChange = (newContent: string) => {
    const updated = sections.map((s) =>
      s.id === selectedSectionId
        ? { ...s, content: newContent, edited: true }
        : s
    );
    setSections(updated);
    updateContentFromSections(updated);
  };

  // Update markdown content from sections
  const updateContentFromSections = (updatedSections: EditorSection[]) => {
    const markdown = updatedSections
      .map((s) => `## ${s.title}\n\n${s.content}`)
      .join('\n\n');
    setLocalMarkdown(markdown);
    setFinalContent(markdown);
    setDocumentContent(markdown);
    setHasChanges(true);
  };

  const handleFinalizeDocument = async (reviewedChanges: Change[], originalContent: string, policyName: string) => {
    if (isFinalizing) return;
    setIsFinalizing(true);
    setIsLoading(true);

    try {
      // Only send accepted changes to the AI
      const acceptedChanges = reviewedChanges.filter(c => c.status === 'accepted');

      if (acceptedChanges.length === 0) {
        // No changes to apply, just use original content
        setFinalContent(originalContent);
        setLocalMarkdown(originalContent); // Initialize Content Editor with original content
        extractPages(originalContent, []);
        setIsLoading(false);
        setIsFinalizing(false);
        return;
      }

      // Call backend to finalize policy using OpenRouter AI
      const { finalizePolicy } = await import('../services/quickCompareService');
      const policyReviewId = sessionStorage.getItem('policy_review_id') || undefined;
      const result = await finalizePolicy(
        originalContent,
        acceptedChanges,
        policyName,
        user?.id,
        orgId || undefined,
        policyReviewId
      );

      // use the returned content directly
      const finalMarkdown = result.content;

      setFinalContent(finalMarkdown);
      setLocalMarkdown(finalMarkdown); // Initialize Content Editor with generated content
      extractPages(finalMarkdown, reviewedChanges);

      // The finalize-policy endpoint now handles updating step to 4 and status to 'completed'
      // No need for a separate call to /finalize endpoint
      console.log('✅ Workflow completed - finalize-policy handled all updates');

      toast({
        title: "Final Policy Generated",
        description: "AI has successfully merged your approved changes into the document.",
      });

      // Clear session storage after successful finalization
      sessionStorage.removeItem('policyReviewId');
      sessionStorage.removeItem('reviewChanges');
      sessionStorage.removeItem('policyMarkdownUrl');
      sessionStorage.removeItem('originalContent');
      sessionStorage.removeItem('policy_review_id');
      console.log('[PreviewExport] 🧹 Session storage cleared after finalization');

    } catch (error) {
      console.error('Error finalizing document with AI:', error);
      toast({
        title: "Finalization Failed",
        description: "AI failed to merge changes. Using fallback local merge.",
        variant: "destructive",
      });

      // Fallback to local merge if AI fails
      generateFinalContentFallback(reviewedChanges, originalContent);
    } finally {
      setIsLoading(false);
      setIsFinalizing(false);
    }
  };

  const generateFinalContentFallback = (reviewedChanges: Change[], originalContent: string) => {
    // Process the content based on accepted/reverted changes
    let processedContent = originalContent;

    // Apply accepted changes and remove reverted changes
    reviewedChanges.forEach(change => {
      if (change.status === 'accepted') {
        if (change.newContent && change.oldContent) {
          // Replace old content with new content
          processedContent = processedContent.replace(
            change.oldContent,
            change.newContent
          );
        }
      }
    });

    // Clean up any remaining highlighting
    processedContent = processedContent
      .replace(/<span class="highlight-addition">(.*?)<\/span>/g, '$1')
      .replace(/<span class="highlight-modification">(.*?)<\/span>/g, '$1')
      .replace(/<span class="highlight-deletion">.*?<\/span>/g, '');

    setFinalContent(processedContent);
    setLocalMarkdown(processedContent); // Initialize Content Editor with fallback content
    extractPages(processedContent, reviewedChanges);
    setIsLoading(false);
  };

  const handleExport = async (format: 'pdf' | 'docx') => {
    setIsExporting(true);

    // Get client info from session storage
    const clientLogo = sessionStorage.getItem('scopingai_client_logo');
    const clientName = sessionStorage.getItem('scopingai_client_name') || 'Client';

    try {
      if (format === 'pdf') {
        const filename = `audit-document-final.pdf`;

        // Use branding settings from state
        const unifiedTheme = {
          fontFamily: fontFamily,
          headingColor: primaryColor,
          subheadingColor: secondaryColor,
          textColor: defaultColor,
          accentColor: primaryColor,
          backgroundColor: "#ffffff",
          logo: logoPreview || clientLogo || "",
          brandName: clientName || "Client",
        };

        // Convert content to blocks for PDF generation
        const blocks = convertContentToBlocks(finalContent);
        const documentData = {
          title: "",
          client: clientName,
          sections: [
            {
              title: "",
              content: finalContent,
              blocks: blocks
            }
          ]
        };

        // Use content-based PDF generator for proper text extraction
        await generatePDFFromPreview(
          documentData,
          unifiedTheme,
          filename
        );

        toast({
          title: "PDF Downloaded",
          description: "Your document has been exported as PDF.",
        });
      } else {
        const filename = `audit-document-final.docx`;

        // Use branding settings from state
        const unifiedTheme = {
          fontFamily: fontFamily,
          headingColor: primaryColor,
          subheadingColor: secondaryColor,
          textColor: defaultColor,
          accentColor: primaryColor,
          backgroundColor: "#ffffff",
          logo: logoPreview || clientLogo || "",
          brandName: clientName || "Client",
        };

        // Convert the final content (markdown) into blocks for the DOCX generator
        const blocks = convertContentToBlocks(finalContent);
        const documentData = {
          title: "",
          client: clientName,
          sections: [
            {
              title: "",
              content: finalContent,
              blocks: blocks
            }
          ]
        };

        await generateEnhancedDOCX(
          documentData,
          unifiedTheme,
          filename
        );

        toast({
          title: "DOCX Downloaded",
          description: "Your document has been exported as Word document.",
        });
      }
    } catch (error) {
      console.error(`Error exporting ${format}:`, error);
      toast({
        title: "Export Failed",
        description: `There was an error generating your ${format.toUpperCase()} file.`,
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Save to Policy Repository (for create-policy flow)
  const handleSaveToRepository = async () => {
    const createPolicyReviewId = sessionStorage.getItem('create_policy_policy_review_id');
    if (!createPolicyReviewId) {
      toast({
        title: "Error",
        description: "No policy record found to save.",
        variant: "destructive",
      });
      return;
    }

    setIsSavingToRepository(true);
    try {
      await finalizePolicyGeneration(createPolicyReviewId, user?.id, orgId);
      setSavedToRepository(true);

      toast({
        title: "Saved to Policy Repository",
        description: "Your policy has been saved and marked as completed.",
      });

      // Clear create-policy session storage after successful save
      sessionStorage.removeItem('create_policy_policy_review_id');
      sessionStorage.removeItem('create_policy_template_id');
      sessionStorage.removeItem('create_policy_template_name');
      sessionStorage.removeItem('create_policy_policy_name');
      sessionStorage.removeItem('create_policy_generated_content');
      sessionStorage.removeItem('create_policy_additional_prompt');
    } catch (error) {
      console.error('Error saving to repository:', error);
      toast({
        title: "Save Failed",
        description: "Failed to save policy to repository. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSavingToRepository(false);
    }
  };

  // Export as Markdown
  const handleExportMarkdown = () => {
    try {
      const content = finalContent || documentContent;
      const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${policyTitle || 'document'}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Markdown Downloaded",
        description: "Your document has been exported as Markdown.",
      });
    } catch (error) {
      console.error('Error exporting markdown:', error);
      toast({
        title: "Export Failed",
        description: "There was an error generating your Markdown file.",
        variant: "destructive",
      });
    }
  };

  const getChangesSummary = () => {
    const accepted = changes.filter(c => c.status === 'accepted').length;
    const reverted = changes.filter(c => c.status === 'reverted').length;
    return { accepted, reverted, total: changes.length };
  };

  const summary = getChangesSummary();

  // Handler for logo file upload
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };


  // Save branding data to backend
  const handleSaveBranding = async () => {
    if (!policyId) {
      toast({
        title: "Cannot save branding",
        description: "Please save the document first.",
        variant: "destructive",
      });
      return;
    }

    setIsSavingBranding(true);
    try {
      // Upload logo file if it exists
      let logoUrl = logoPreview;
      let logoFilePath: string | undefined;

      if (logoFile) {
        const formData = new FormData();
        formData.append('logo', logoFile);

        const uploadResponse = await fetch(`/api/revisr/policy-review/${policyId}/branding/upload`, {
          method: 'POST',
          headers: {
            'x-user-id': user?.id || '',
            'x-org-id': orgId,
          },
          body: formData,
        });

        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          if (uploadData.files?.logoUrl) {
            logoFilePath = uploadData.files.logoUrl;
            // Get public URL for the uploaded file
            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
            logoUrl = `${supabaseUrl}/storage/v1/object/public/knowledge_docs/${logoFilePath}`;
          }
        }
      }

      const brandingData = {
        logoPreview: logoUrl,
        logoUrl: logoFilePath,
        fontFamily,
        primaryColor,
        secondaryColor,
        defaultColor,
      };

      const response = await fetch(`/api/revisr/policy-review/${policyId}/branding`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': user?.id || '',
          'x-org-id': orgId,
        },
        body: JSON.stringify({
          branding_data: brandingData,
        }),
      });

      if (response.ok) {
        toast({
          title: "Branding saved",
          description: "Your branding settings have been saved.",
        });
      } else {
        throw new Error('Failed to save branding');
      }
    } catch (error) {
      console.error('Error saving branding:', error);
      toast({
        title: "Error",
        description: "Failed to save branding settings.",
        variant: "destructive",
      });
    } finally {
      setIsSavingBranding(false);
    }
  };

  // Save policy name to backend
  const handleSavePolicyName = async () => {
    if (!policyId || !editedPolicyName.trim()) {
      toast({
        title: "Cannot update",
        description: "Please enter a valid policy name.",
        variant: "destructive",
      });
      return;
    }

    setIsSavingPolicyName(true);
    try {
      const response = await fetch(`/api/revisr/policy-review/${policyId}/policy-name`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': user?.id || '',
          'x-org-id': orgId,
        },
        body: JSON.stringify({
          policy_name: editedPolicyName,
        }),
      });

      if (response.ok) {
        setPolicyTitle(editedPolicyName);
        toast({
          title: "Policy name updated",
          description: "The policy name has been updated successfully.",
        });
      } else {
        throw new Error('Failed to update policy name');
      }
    } catch (error) {
      console.error('Error updating policy name:', error);
      toast({
        title: "Error",
        description: "Failed to update policy name.",
        variant: "destructive",
      });
    } finally {
      setIsSavingPolicyName(false);
    }
  };

  // Handle editor changes
  const handleEditorChange = (content: string) => {
    setLocalMarkdown(content);
    setHasChanges(true);
  };

  // Save edited markdown to backend
  const handleSaveChanges = async () => {
    if (!policyId || !localMarkdown) return;

    setIsSavingMarkdown(true);
    try {
      const result = await updatePolicyMarkdown(policyId, localMarkdown, user?.id, orgId);

      if (result.markdown_url) {
        setFinalContent(localMarkdown);
        setDocumentContent(localMarkdown);
        // Re-extract pages for the preview
        extractPages(localMarkdown, []);
        setHasChanges(false);
        toast({
          title: "Changes saved",
          description: "Policy content has been updated successfully.",
        });
      }
    } catch (error) {
      console.error('Error saving policy markdown:', error);
      toast({
        title: "Error",
        description: "Failed to save policy changes.",
        variant: "destructive",
      });
    } finally {
      setIsSavingMarkdown(false);
    }
  };

  // Initialize editedPolicyName when policyTitle is set
  useEffect(() => {
    if (policyTitle && !editedPolicyName) {
      setEditedPolicyName(policyTitle);
    }
  }, [policyTitle]);

  // Get selected section
  const selectedSection = sections.find(s => s.id === selectedSectionId) || null;

  if (isLoading) {
    return (
      <ScopingAILayout hideMobileBar>
        <div className="min-h-screen bg-[#FCFBF4] flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Preparing compliance report...</p>
          </div>
        </div>
      </ScopingAILayout>
    );
  }

  return (
    <ScopingAILayout hideMobileBar>
      <div className="min-h-screen bg-[#FCFBF4] overflow-x-hidden">
        {/* Header */}
        <div className="mb-6 md:mb-8 pt-4 md:pt-6 px-4 md:px-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 md:mb-6">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                onClick={() => navigate(sourceRoute)}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 px-0 min-h-[44px]"
              >
                <ArrowLeft className="h-5 w-5" />
                <span className="font-medium">Back</span>
              </Button>
              <div className="flex items-center gap-3">
                <h1 className="text-xl md:text-2xl font-bold text-gray-900 truncate max-w-[200px] sm:max-w-none">{policyTitle || "Document Preview"}</h1>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap" data-testid="export-actions">
              {/* Save to Policy Repository button (create-policy flow only) */}
              {workflowType === 'create_policy' && sessionStorage.getItem('create_policy_policy_review_id') && !savedToRepository && (
                <Button
                  onClick={handleSaveToRepository}
                  disabled={isSavingToRepository}
                  variant="outline"
                  className="gap-2 border-[#33b864] text-[#33b864] hover:bg-[#33b864]/10"
                >
                  {isSavingToRepository ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  {isSavingToRepository ? 'Saving...' : 'Save to Repository'}
                </Button>
              )}
              {savedToRepository && (
                <Badge variant="secondary" className="gap-1.5 py-1.5 px-3 bg-green-50 text-green-700 border-green-200">
                  <Check className="h-3.5 w-3.5" />
                  Saved to Repository
                </Badge>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    className="bg-[#33b864] text-white hover:bg-[#2da358] gap-2 px-6"
                    disabled={isExporting}
                  >
                    {isExporting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                    Export
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => handleExport('pdf')} className="py-3">
                    <FileText className="h-4 w-4 mr-2 text-red-500" />
                    <span>Export as PDF</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport('docx')} className="py-3">
                    <FileText className="h-4 w-4 mr-2 text-blue-500" />
                    <span>Export as DOCX</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportMarkdown} className="py-3">
                    <FileText className="h-4 w-4 mr-2 text-gray-500" />
                    <span>Export as Markdown</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex items-center overflow-x-auto border-b border-gray-200 mb-6 md:mb-8 px-4 md:px-6 gap-2 md:gap-0 md:space-x-8 scrollbar-hidden">
            {[
              { id: 'preview', label: 'Preview', icon: Eye },
              { id: 'editor', label: 'Content Editor', icon: Edit3 },
              { id: 'info', label: 'Document Info', icon: FileText },
              { id: 'branding', label: 'Theme & Branding', icon: CheckCircle },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 transition-all duration-200 ${activeTab === tab.id
                  ? "border-[#33b864] text-[#33b864]"
                  : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
              >
                <tab.icon className="h-4 w-4" />
                <span className="font-medium">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Conditional Tab Content */}
        {activeTab === 'preview' && (
          <>
            {isLoading ? (
              <div className="flex items-center justify-center h-[calc(100vh-280px)]">
                <div className="text-center">
                  <Loader2 className="h-12 w-12 animate-spin text-[#33b864] mx-auto mb-4" />
                  <p className="text-gray-600">Loading policy content...</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6 pb-4 px-4 md:px-6">
            {/* Left Column - Section List (hidden on mobile, not needed for preview) */}
            <div className="hidden lg:block lg:col-span-3 overflow-hidden">
              <SortableSectionList
                sections={sections}
                selectedSectionId={selectedSectionId}
                onSectionSelect={handleSectionSelect}
                onSectionReorder={handleSectionReorder}
                onSectionTitleChange={handleSectionTitleChange}
                onSectionDelete={handleSectionDelete}
                onSectionAdd={handleSectionAdd}
                onSectionAddAfter={handleSectionAddAfter}
                onSectionEdit={handleSectionEdit}
              />
            </div>

            {/* Right Column - Document Preview (full width on mobile, 9/12 on desktop) */}
            <div className="col-span-1 lg:col-span-9">

              <div className="bg-[#f3f4f6] rounded-xl pt-6 md:pt-12 pb-8 px-3 md:px-8 h-[calc(100vh-280px)] overflow-hidden shadow-inner flex justify-center">
                <ScrollArea className="h-full w-full max-w-[850px]">
                  <div
                    className="bg-white shadow-2xl mx-auto min-h-[500px] md:min-h-[1100px] p-5 md:p-[80px] relative transition-all duration-300"
                    ref={documentContainerRef}
                    style={{ fontFamily: fontFamily }}
                  >
                    {/* Logo at top if uploaded */}
                    {logoPreview && (
                      <div className="mb-8 flex justify-start">
                        <img src={logoPreview} alt="Logo" className="max-h-16 object-contain" />
                      </div>
                    )}
                    {/* Render selected section or full document */}
                    <div>
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          h1: ({ children }) => (
                            <h1 style={{ color: primaryColor, fontFamily, fontSize: '2rem', fontWeight: 700, marginBottom: '1rem', marginTop: '1.5rem', lineHeight: 1.3, borderBottom: `2px solid ${primaryColor}20`, paddingBottom: '0.5rem' }}>{children}</h1>
                          ),
                          h2: ({ children }) => (
                            <h2 style={{ color: secondaryColor, fontFamily, fontSize: '1.5rem', fontWeight: 600, marginBottom: '0.75rem', marginTop: '1.5rem', lineHeight: 1.3, borderBottom: `2px solid ${secondaryColor}15`, paddingBottom: '0.4rem' }}>{children}</h2>
                          ),
                          h3: ({ children }) => (
                            <h3 style={{ color: secondaryColor, fontFamily, fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem', marginTop: '1.25rem', lineHeight: 1.3 }}>{children}</h3>
                          ),
                          h4: ({ children }) => (
                            <h4 style={{ color: secondaryColor, fontFamily, fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.4rem', marginTop: '1rem' }}>{children}</h4>
                          ),
                          p: ({ children }) => (
                            <p style={{ color: defaultColor, fontFamily, fontSize: '1rem', lineHeight: 1.75, marginBottom: '0.75rem' }}>{children}</p>
                          ),
                          ul: ({ children }) => (
                            <ul style={{ color: defaultColor, fontFamily, fontSize: '1rem', lineHeight: 1.75, paddingLeft: '1.75rem', marginBottom: '0.75rem', listStyleType: 'disc' }}>{children}</ul>
                          ),
                          ol: ({ children }) => (
                            <ol style={{ color: defaultColor, fontFamily, fontSize: '1rem', lineHeight: 1.75, paddingLeft: '1.75rem', marginBottom: '0.75rem', listStyleType: 'decimal' }}>{children}</ol>
                          ),
                          li: ({ children }) => (
                            <li style={{ color: defaultColor, fontFamily, marginBottom: '0.35rem', fontSize: '1rem' }}>{children}</li>
                          ),
                          strong: ({ children }) => (
                            <strong style={{ color: primaryColor, fontWeight: 600 }}>{children}</strong>
                          ),
                          blockquote: ({ children }) => (
                            <blockquote style={{ borderLeft: `4px solid ${primaryColor}`, paddingLeft: '1.25rem', margin: '1rem 0', fontStyle: 'italic', color: '#6b7280', fontSize: '1rem' }}>{children}</blockquote>
                          ),
                          hr: () => (
                            <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '1.5rem 0' }} />
                          ),
                          a: ({ href, children }) => (
                            <a href={href} style={{ color: primaryColor, textDecoration: 'underline' }} target={href?.startsWith('http') ? '_blank' : undefined} rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}>{children}</a>
                          ),
                          table: ({ children }) => (
                            <div style={{ overflowX: 'auto', marginBottom: '1.25rem', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem', fontFamily }}>{children}</table>
                            </div>
                          ),
                          thead: ({ children }) => (
                            <thead style={{ backgroundColor: `${primaryColor}08` }}>{children}</thead>
                          ),
                          th: ({ children }) => (
                            <th style={{ backgroundColor: `${primaryColor}10`, color: primaryColor, fontWeight: 600, padding: '0.6rem 0.85rem', textAlign: 'left' as const, borderBottom: `2px solid ${primaryColor}30` }}>{children}</th>
                          ),
                          td: ({ children }) => (
                            <td style={{ padding: '0.6rem 0.85rem', borderBottom: '1px solid #e5e7eb', color: defaultColor }}>{children}</td>
                          ),
                        }}
                      >
                        {selectedSection
                          ? `## ${selectedSection.title}\n\n${selectedSection.content}`
                          : finalContent}
                      </ReactMarkdown>
                    </div>
                  </div>
                </ScrollArea>
              </div>
            </div>
          </div>
            )}
          </>
        )}
        {activeTab === 'editor' && (
          <>
            {isLoading ? (
              <div className="flex items-center justify-center h-[calc(100vh-280px)]">
                <div className="text-center">
                  <Loader2 className="h-12 w-12 animate-spin text-[#33b864] mx-auto mb-4" />
                  <p className="text-gray-600">Loading policy content...</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6 pb-4 px-4 md:px-6 lg:h-[calc(100vh-280px)]">
                {/* Left Column - Section List */}
                <div className="lg:col-span-3 overflow-hidden">
                  <SortableSectionList
                    sections={sections}
                    selectedSectionId={selectedSectionId}
                    onSectionSelect={handleSectionSelect}
                    onSectionReorder={handleSectionReorder}
                    onSectionTitleChange={handleSectionTitleChange}
                    onSectionDelete={handleSectionDelete}
                    onSectionAdd={handleSectionAdd}
                    onSectionAddAfter={handleSectionAddAfter}
                    onSectionEdit={handleSectionEdit}
                  />
                </div>

                {/* Right Column - Section Editor */}
                <div className="lg:col-span-9 flex flex-col h-full">
                  {selectedSection ? (
                    <div className="flex-1 flex flex-col border rounded-xl bg-white shadow-sm overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
                        <h3 className="font-semibold text-gray-900">
                          {selectedSection.title}
                        </h3>
                        <Button
                          onClick={handleSaveChanges}
                          disabled={isSavingMarkdown || !hasChanges}
                          size="sm"
                          className="bg-[#33b864] hover:bg-[#2da358] text-white gap-2"
                        >
                          {isSavingMarkdown ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                          {isSavingMarkdown ? 'Saving...' : 'Save Changes'}
                        </Button>
                      </div>
                      <div className="flex-1 overflow-auto">
                        <YooptaMarkdownEditor
                          key={`editor-${selectedSectionId}-${editorKey}`}
                          markdownContent={selectedSection.content}
                          onChange={handleSectionContentChange}
                          placeholder="Start writing section content..."
                          userId={user?.id}
                          bucketName="policy_media"
                          autoFocus
                          className="h-full"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="flex-1 flex items-center justify-center border rounded-xl bg-white">
                      <div className="text-center text-gray-400">
                        <FileText className="h-10 w-10 mx-auto mb-3 opacity-30" />
                        <p className="text-sm">Select a section to edit</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'info' && (
          <div className="px-6 pb-20">
            <Card className="max-w-3xl mx-auto">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Info className="h-5 w-5 text-blue-500" />
                  Document Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Policy Name */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Policy Name</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={editedPolicyName}
                      onChange={(e) => setEditedPolicyName(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#33b864] focus:border-transparent outline-none"
                      placeholder="Enter policy name"
                    />
                    <Button
                      onClick={handleSavePolicyName}
                      disabled={isSavingPolicyName || !policyId}
                      className="bg-[#33b864] hover:bg-[#2da358] text-white gap-2"
                    >
                      {isSavingPolicyName ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                      Save
                    </Button>
                  </div>
                  {!policyId && (
                    <p className="text-xs text-amber-600">Note: Policy must be finalized before editing the name.</p>
                  )}
                </div>

                {/* Document Metadata */}
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Status</label>
                    <div className="flex items-center gap-2">
                      <Badge className={policyStatus === 'completed' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                        {policyStatus || 'Draft'}
                      </Badge>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total Pages</label>
                    <p className="text-sm text-gray-700">{pages.length} pages</p>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Changes Applied</label>
                    <p className="text-sm text-gray-700">{getChangesSummary().accepted} accepted, {getChangesSummary().reverted} reverted</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'branding' && (
          <div className="px-6 pb-20">
            <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Logo Upload */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Image className="h-5 w-5 text-blue-500" />
                    Company Logo
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#33b864] transition-colors">
                      <input
                        type="file"
                        id="logo-upload"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                      <label htmlFor="logo-upload" className="cursor-pointer">
                        {logoPreview ? (
                          <img src={logoPreview} alt="Logo Preview" className="max-h-24 mx-auto object-contain" />
                        ) : (
                          <div className="space-y-2">
                            <Upload className="h-10 w-10 mx-auto text-gray-400" />
                            <p className="text-sm text-gray-500">Click to upload logo</p>
                            <p className="text-xs text-gray-400">PNG, JPG up to 5MB</p>
                          </div>
                        )}
                      </label>
                    </div>
                    {logoPreview && (
                      <Button variant="outline" size="sm" onClick={() => { setLogoPreview(''); setLogoFile(null); }}>
                        Remove Logo
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Font Selection */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Type className="h-5 w-5 text-green-500" />
                    Font Style
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Select value={fontFamily} onValueChange={setFontFamily}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select font" />
                    </SelectTrigger>
                    <SelectContent>
                      {BrandingService.getAvailableFonts().map((font) => (
                        <SelectItem key={font.value} value={font.value}>
                          {font.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-2">Select a font family for the document</p>
                </CardContent>
              </Card>

              {/* Color Settings */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Palette className="h-5 w-5 text-orange-500" />
                    Color Settings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-gray-700">Primary Color (Headings)</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={primaryColor}
                          onChange={(e) => setPrimaryColor(e.target.value)}
                          className="w-10 h-10 rounded cursor-pointer border-0"
                        />
                        <span className="text-xs font-mono text-gray-500">{primaryColor}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-gray-700">Secondary Color (Subheadings)</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={secondaryColor}
                          onChange={(e) => setSecondaryColor(e.target.value)}
                          className="w-10 h-10 rounded cursor-pointer border-0"
                        />
                        <span className="text-xs font-mono text-gray-500">{secondaryColor}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-gray-700">Body Text Color</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={defaultColor}
                          onChange={(e) => setDefaultColor(e.target.value)}
                          className="w-10 h-10 rounded cursor-pointer border-0"
                        />
                        <span className="text-xs font-mono text-gray-500">{defaultColor}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Save Branding Button */}
              <div className="md:col-span-2">
                <Button
                  onClick={handleSaveBranding}
                  disabled={isSavingBranding || !policyId}
                  className="w-full bg-[#33b864] hover:bg-[#2da358] text-white py-3 gap-2"
                >
                  {isSavingBranding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save Branding Settings
                </Button>
                {!policyId && (
                  <p className="text-xs text-amber-600 text-center mt-2">Note: Policy must be finalized before saving branding settings.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons - Hidden for now as they are not in the target UI, 
              Export is the primary action in Header */}
        {/* <div className="flex justify-center items-center gap-4 mt-8">...</div> */}
      </div>
    </ScopingAILayout >
  );
}
